import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const BookingTypeEnum = z.enum([
  "pool_table",
  "food_and_drinks",
  "sports_viewing",
  "darts",
  "group_booking",
  "karaoke",
]);

const GuestInput = z.object({
  full_name: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(200).optional().nullable(),
  phone: z.string().trim().min(6).max(32),
  marketing_consent: z.boolean().default(false),
});

const JoinWaitlistInput = z.object({
  booking_type: BookingTypeEnum,
  desired_at: z.string(),
  party_size: z.number().int().min(1).max(40),
  notes: z.string().trim().max(500).optional().nullable(),
  guest: GuestInput,
});

type JoinWaitlistOutput = {
  ok: true;
  id: string;
  desired_at: string;
  booking_type: z.infer<typeof BookingTypeEnum>;
  party_size: number;
};

async function upsertGuest(
  admin: Awaited<
    typeof import("@/integrations/supabase/client.server")
  >["supabaseAdmin"],
  guest: z.infer<typeof GuestInput>,
): Promise<string> {
  const { data: existing } = await admin
    .from("guests")
    .select("id")
    .eq("phone", guest.phone)
    .maybeSingle();

  if (existing?.id) {
    await admin
      .from("guests")
      .update({
        full_name: guest.full_name,
        ...(guest.email ? { email: guest.email } : {}),
        marketing_consent: guest.marketing_consent,
      })
      .eq("id", existing.id);
    return existing.id;
  }

  const { data: created, error } = await admin
    .from("guests")
    .insert({
      full_name: guest.full_name,
      email: guest.email ?? null,
      phone: guest.phone,
      marketing_consent: guest.marketing_consent,
    })
    .select("id")
    .single();
  if (error || !created) throw new Error("Failed to save guest details");
  return created.id;
}

function assertValidDesiredAt(iso: string) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) throw new Error("Invalid date/time");
  if (t <= Date.now()) throw new Error("Pick a time in the future");
  if (t > Date.now() + 180 * 24 * 60 * 60 * 1000)
    throw new Error("Pick a date within the next 6 months");
}

/** Public: a guest joins the waitlist when their slot isn't available. */
export const joinWaitlist = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => JoinWaitlistInput.parse(d))
  .handler(async ({ data }): Promise<JoinWaitlistOutput> => {
    assertValidDesiredAt(data.desired_at);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const guestId = await upsertGuest(supabaseAdmin, data.guest);

    const { data: row, error } = await supabaseAdmin
      .from("waitlist")
      .insert({
        guest_id: guestId,
        booking_type: data.booking_type,
        desired_at: data.desired_at,
        party_size: data.party_size,
        notes: data.notes ?? null,
      })
      .select("id")
      .single();
    if (error || !row) throw new Error("Could not join the waitlist");

    return {
      ok: true,
      id: row.id,
      desired_at: data.desired_at,
      booking_type: data.booking_type,
      party_size: data.party_size,
    };
  });

/** Staff: add a guest to the waitlist from the admin dashboard. */
export const adminAddWaitlist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => JoinWaitlistInput.parse(d))
  .handler(async ({ data, context }) => {
    assertValidDesiredAt(data.desired_at);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const guestId = await upsertGuest(supabaseAdmin, data.guest);

    const { data: row, error } = await context.supabase
      .from("waitlist")
      .insert({
        guest_id: guestId,
        booking_type: data.booking_type,
        desired_at: data.desired_at,
        party_size: data.party_size,
        notes: data.notes ?? null,
      })
      .select("id")
      .single();
    if (error || !row) throw new Error(error?.message ?? "Could not add to waitlist");
    return { ok: true as const, id: row.id };
  });

/** Staff: confirm a waitlist entry only when a resource is free. */
export const convertWaitlistToReservation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; duration_minutes?: number }) =>
    z
      .object({
        id: z.string().uuid(),
        duration_minutes: z.number().int().min(90).max(120).default(90),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { data: entry, error: eErr } = await context.supabase
      .from("waitlist")
      .select("id,guest_id,booking_type,desired_at,party_size,notes,source")
      .eq("id", data.id)
      .maybeSingle();
    if (eErr) throw new Error(eErr.message);
    if (!entry) throw new Error("Waitlist entry not found");

    const { requiresEmailBooking } = await import("@/lib/booking-policy");
    if (requiresEmailBooking(entry.booking_type, entry.party_size)) {
      throw new Error("Groups over 10 and Karaoke Terrace private hire must email bookings@plunkets.ie.");
    }
    const { findAvailableTables } = await import("@/lib/availability.server");
    const tableIds = await findAvailableTables(context.supabase, entry.booking_type, entry.desired_at, data.duration_minutes, entry.party_size);
    if (!tableIds.length) throw new Error("No free tables at this time. The guest remains on the waitlist.");
    const { confirmBooking } = await import("@/lib/confirm-booking.server");
    const reservation = await confirmBooking(context.supabase, {
      booking_type: entry.booking_type, guest_id: entry.guest_id,
      starts_at: entry.desired_at, duration_minutes: data.duration_minutes,
      party_size: entry.party_size, notes: entry.notes, source: entry.source,
    }, tableIds, entry.id);
    const { data: guest } = await context.supabase.from("guests").select("full_name,email").eq("id", entry.guest_id ?? "").maybeSingle();
    const { sendBookingEmail } = await import("@/lib/booking-emails.server");
    await sendBookingEmail({ template: "booking-confirmation", to: guest?.email ?? "", reservationId: reservation.id,
      reference: reservation.reference, fullName: guest?.full_name, startsAt: entry.desired_at,
      partySize: entry.party_size, bookingType: entry.booking_type, durationMinutes: data.duration_minutes, confirmed: true });

    return { ok: true as const, id: reservation.id, reference: reservation.reference };
  });
